/**
 * Deen Over Dunya — API client
 *
 * A small fetch wrapper. The API speaks camelCase JSON, so responses are
 * returned as-is and typed with the generated schema types. Retries and
 * caching are handled by TanStack Query (see ./queries.ts).
 */

/** Same-origin by default: CloudFront routes /api/* to the API, and Vite proxies it locally. */
export const API_BASE_URL = (import.meta.env.VITE_API_URL || "/api/v1").replace(/\/$/, "");

const TIMEOUT_MS = 30_000;

/* ============================================
   Errors
   ============================================ */

/** Error body returned by the API: {"error": "...", "detail": {...}}. */
interface ErrorBody {
  error?: string;
  detail?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }

  get isRateLimited() {
    return this.status === 429;
  }
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.isNotFound;
}

/** A clarifier entry that was withdrawn: the API answers 410 with the reason. */
export interface Withdrawn {
  slug: string;
  title: string;
  withdrawnAt: string | null;
  note: string | null;
}

export function withdrawnEntry(error: unknown): Withdrawn | null {
  return error instanceof ApiError && error.status === 410 ? (error.detail as Withdrawn) : null;
}

/** A user-facing message for any thrown value. */
export function errorMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return "Can't reach the server. Check your connection and try again.";
    if (error.status >= 500) return "The server ran into a problem. Please try again shortly.";
    return error.message || fallback;
  }
  return fallback;
}

/* ============================================
   Auth token
   ============================================ */

type TokenProvider = () => string | null | undefined;
let tokenProvider: TokenProvider = () => null;

/** Registered by the auth provider; returns the current bearer token, if any. */
export function setTokenProvider(provider: TokenProvider) {
  tokenProvider = provider;
}

/* ============================================
   Requests
   ============================================ */

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${API_BASE_URL}${path}`;
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${url}?${query}` : url;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  params?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", params, body, signal } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = tokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;

  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new ApiError(0, "Network error", err);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data: unknown = text ? safeJson(text) : undefined;

  if (!response.ok) {
    const errorBody = (data ?? {}) as ErrorBody;
    throw new ApiError(
      response.status,
      typeof errorBody.error === "string" ? errorBody.error : response.statusText,
      errorBody.detail,
    );
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
